import React, { useState, useEffect, useMemo } from 'react';
import './AdminUsers.css';
import './InstructorCourses.css';
import { apiFetch } from './api';
import { useActiveCourse } from './ActiveCourseContext';

function InstructorCourses({ instructorId }) {
  const [courses, setCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseStudents, setCourseStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [exams, setExams] = useState([]);
  const [expandedPrograms, setExpandedPrograms] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const activeCourseContext = useActiveCourse();

  useEffect(() => {
    if (instructorId !== null && instructorId !== undefined) {
      fetchCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorId]);

  useEffect(() => {
    if (instructorId === null || instructorId === undefined) return;
    const interval = setInterval(() => {
      fetchCourses(true).catch((err) => console.error('Otomatik yenileme hatası:', err));
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorId]);

  const fetchCourses = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await apiFetch(`/instructor/courses`);
      const data = await response.json();
      if (data.success) {
        setCourses(data.courses || []);
      } else {
        if (!silent) setError(data.message || 'Dersler yüklenemedi');
      }
    } catch (err) {
      if (!silent) setError('Bağlantı hatası');
      console.error('Error:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchCourseDetails = async (courseId) => {
    try {
      setLoadingStudents(true);
      const studentsResponse = await apiFetch(`/instructor/students?course_id=${courseId}`);
      const studentsData = await studentsResponse.json();
      const examsResponse = await apiFetch(`/instructor/exams`);
      const examsData = await examsResponse.json();

      if (studentsData.success && examsData.success) {
        const courseExams = (examsData.exams || []).filter((exam) => exam.course_id === courseId);
        setExams(courseExams);

        const examDataMap = {};
        for (const exam of courseExams) {
          try {
            const eRes = await apiFetch(`/instructor/exams/${exam.exam_id}/students`);
            const eData = await eRes.json();
            examDataMap[exam.exam_id] = {};
            if (eData.success) {
              (eData.students || []).forEach((student) => {
                examDataMap[exam.exam_id][student.student_number] = {
                  attempt_status: student.attempt_status,
                  attempt_id: student.attempt_id,
                  total_points: student.total_points,
                };
              });
            }
          } catch (err) {
            examDataMap[exam.exam_id] = {};
          }
        }

        const studentsWithGrades = (studentsData.students || []).map((student) => {
          const grades = courseExams.map((exam) => {
            const examData = examDataMap[exam.exam_id]?.[student.student_number];
            if (!examData || !examData.attempt_status) {
              return { exam_id: exam.exam_id, exam_title: exam.title, status: 'not_attempted', total_points: null };
            }
            if (examData.total_points !== null && examData.total_points !== undefined) {
              return { exam_id: exam.exam_id, exam_title: exam.title, status: 'completed', total_points: examData.total_points };
            }
            if (examData.attempt_status === 'Submitted') {
              return { exam_id: exam.exam_id, exam_title: exam.title, status: 'submitted_no_result', total_points: null };
            }
            return { exam_id: exam.exam_id, exam_title: exam.title, status: examData.attempt_status, total_points: null };
          });
          return { ...student, grades };
        });

        setCourseStudents(studentsWithGrades);
      }
    } catch (err) {
      console.error('Ders detayları yüklenemedi:', err);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleCourseClick = (course) => {
    setSelectedCourse(course);
    fetchCourseDetails(course.course_id);
    if (activeCourseContext) activeCourseContext.setActiveCourseId(course.course_id);
  };

  const handleBack = () => {
    setSelectedCourse(null);
    setCourseStudents([]);
    setExams([]);
    if (activeCourseContext) activeCourseContext.clearActiveCourse();
  };

  useEffect(() => {
    if (!activeCourseContext) return;
    const nextCourseId = activeCourseContext.activeCourseId;
    if (!nextCourseId) return;
    if (String(selectedCourse?.course_id) === String(nextCourseId)) return;
    const nextCourse = courses.find((course) => String(course.course_id) === String(nextCourseId));
    if (!nextCourse) return;
    setSelectedCourse(nextCourse);
    fetchCourseDetails(nextCourse.course_id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCourseContext?.activeCourseId, courses]);

  const q = searchQuery.toLocaleLowerCase('tr');
  const filteredCourses = useMemo(() => {
    if (!q) return courses;
    return courses.filter((c) =>
      (c.course_code || '').toLocaleLowerCase('tr').includes(q) ||
      (c.course_name || '').toLocaleLowerCase('tr').includes(q) ||
      (c.faculty_name || '').toLocaleLowerCase('tr').includes(q) ||
      (c.program_name || '').toLocaleLowerCase('tr').includes(q)
    );
  }, [courses, q]);

  // Group courses by program
  const grouped = useMemo(() => {
    const map = new Map();
    for (const c of filteredCourses) {
      const key = c.program_id || c.program_name || 'Diğer';
      if (!map.has(key)) {
        map.set(key, {
          key,
          program_name: c.program_name || 'Diğer',
          faculty_name: c.faculty_name || '',
          courses: [],
        });
      }
      map.get(key).courses.push(c);
    }
    const arr = Array.from(map.values());
    arr.sort((a, b) => (a.program_name || '').localeCompare(b.program_name || '', 'tr'));
    arr.forEach((g) => {
      g.courses.sort((a, b) => (a.course_code || a.course_name || '').localeCompare(b.course_code || b.course_name || '', 'tr'));
    });
    return arr;
  }, [filteredCourses]);

  // Auto-expand all groups when searching
  const effectivelyExpanded = (key) => {
    if (q) return true;
    if (allExpanded) return true;
    return !!expandedPrograms[key];
  };

  const toggleProgram = (key) => {
    setExpandedPrograms((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAll = () => {
    setAllExpanded(true);
    const all = {};
    grouped.forEach((g) => { all[g.key] = true; });
    setExpandedPrograms(all);
  };

  const collapseAll = () => {
    setAllExpanded(false);
    setExpandedPrograms({});
  };

  if (loading) {
    return (
      <div className="au-page">
        <div className="au-toolbar">
          <h2 className="au-title">Derslerim</h2>
        </div>
        <div className="au-card">
          <div className="au-skeleton">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="au-skeleton-row">
                <div className="au-skeleton-circle" />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div className="au-skeleton-block" style={{ width: '40%' }} />
                  <div className="au-skeleton-block" style={{ width: '25%', height: 11 }} />
                </div>
                <div className="au-skeleton-block" style={{ width: 80, height: 24 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="au-page">
        <div className="au-toolbar">
          <h2 className="au-title">Derslerim</h2>
        </div>
        <div className="au-flash error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      </div>
    );
  }

  if (selectedCourse) {
    return (
      <div className="ic-detail-page">
        <div className="ic-detail-header">
          <button className="ic-back-btn" onClick={handleBack}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Geri
          </button>
          <div className="ic-detail-title-block">
            {selectedCourse.course_code && (
              <span className="ic-detail-code">{selectedCourse.course_code}</span>
            )}
            <h2 className="ic-detail-name">{selectedCourse.course_name}</h2>
          </div>
        </div>

        {loadingStudents ? (
          <div className="au-card">
            <div className="au-skeleton">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="au-skeleton-row">
                  <div className="au-skeleton-block" style={{ width: 100 }} />
                  <div className="au-skeleton-block" style={{ flex: 1 }} />
                  <div className="au-skeleton-block" style={{ width: 80 }} />
                </div>
              ))}
            </div>
          </div>
        ) : courseStudents.length === 0 ? (
          <div className="au-card">
            <div className="au-empty">
              <div className="au-empty-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </svg>
              </div>
              <div className="au-empty-text">Bu derse kayıtlı öğrenci bulunmamaktadır</div>
            </div>
          </div>
        ) : (
          <div className="ic-grades-table-wrap">
            <table className="ic-grades-table">
              <thead>
                <tr>
                  <th>Öğrenci No</th>
                  <th>Ad Soyad</th>
                  {exams.map((exam) => (
                    <th key={exam.exam_id} className="center" style={{ minWidth: 120 }}>
                      {exam.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {courseStudents.map((student) => (
                  <tr key={student.student_id}>
                    <td>{student.student_number}</td>
                    <td style={{ fontWeight: 500 }}>{student.name || '-'}</td>
                    {exams.map((exam) => {
                      const grade = student.grades.find((g) => g.exam_id === exam.exam_id);
                      return (
                        <td key={exam.exam_id} className="center">
                          {!grade || grade.status === 'not_attempted' ? (
                            <span className="ic-grade-absent">Girmedi</span>
                          ) : grade.status === 'completed' && grade.total_points !== null ? (
                            <span className={grade.total_points >= 50 ? 'ic-grade-pass' : 'ic-grade-fail'}>
                              {grade.total_points.toFixed(2)}
                            </span>
                          ) : grade.status === 'submitted_no_result' ? (
                            <span className="ic-grade-pending">Sonuç bekleniyor</span>
                          ) : (
                            <span className="ic-grade-absent">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  const totalCourseCount = courses.length;
  const matchCount = filteredCourses.length;

  return (
    <div className="au-page">
      <div className="au-toolbar">
        <div className="au-toolbar-top">
          <div className="au-title-block">
            <h2 className="au-title">Derslerim</h2>
            <p className="au-subtitle">Programlara tıklayarak ait oldukları dersleri görüntüleyin.</p>
          </div>
          <div className="au-count-pill">
            <span className="au-count-pill-dot" />
            {grouped.length} program · {matchCount} ders
          </div>
        </div>

        <div className="au-search-wrapper">
          <span className="au-search-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            type="text"
            className="au-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Ders kodu, ders adı, fakülte veya bölüm ara..."
          />
          {searchQuery && (
            <button
              type="button"
              className="au-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Aramayı temizle"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {!q && grouped.length > 1 && (
          <div className="ic-quick-actions">
            <button type="button" className="ic-quick-btn" onClick={expandAll}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
              Tümünü Aç
            </button>
            <button type="button" className="ic-quick-btn" onClick={collapseAll}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="18 15 12 9 6 15" />
              </svg>
              Tümünü Kapat
            </button>
          </div>
        )}
      </div>

      {totalCourseCount === 0 ? (
        <div className="ic-empty">
          <div className="ic-empty-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          </div>
          <div className="ic-empty-text">Henüz ders listeniz bulunmamaktadır</div>
        </div>
      ) : grouped.length === 0 ? (
        <div className="ic-empty">
          <div className="ic-empty-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <div className="ic-empty-text">Arama sonucu bulunamadı</div>
          <div className="ic-empty-sub">"{searchQuery}" için sonuç yok. Aramayı değiştirmeyi deneyin.</div>
        </div>
      ) : (
        <div className="ic-groups">
          {grouped.map((g) => {
            const open = effectivelyExpanded(g.key);
            return (
              <div key={g.key} className={`ic-group ${open ? 'open' : ''}`}>
                <button
                  type="button"
                  className="ic-group-header"
                  onClick={() => toggleProgram(g.key)}
                  aria-expanded={open}
                >
                  <span className="ic-group-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                      <path d="M6 12v5c3 3 9 3 12 0v-5" />
                    </svg>
                  </span>
                  <div className="ic-group-title-block">
                    <span className="ic-group-name">{g.program_name}</span>
                    {g.faculty_name && (
                      <span className="ic-group-faculty">{g.faculty_name}</span>
                    )}
                  </div>
                  <span className="ic-group-count">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                    {g.courses.length} ders
                  </span>
                  <span className="ic-group-chevron">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </button>

                {open && (
                  <div className="ic-group-body">
                    {g.courses.map((course) => (
                      <button
                        key={course.course_id}
                        type="button"
                        className="ic-course-card"
                        onClick={() => handleCourseClick(course)}
                      >
                        {course.course_code && (
                          <span className="ic-course-code">{course.course_code}</span>
                        )}
                        <span className="ic-course-name">{course.course_name}</span>
                        <span className="ic-course-arrow">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                          </svg>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default InstructorCourses;
